import { jsx, jsxs } from "react/jsx-runtime";
import { AdaptParentContext } from "@tamagui/adapt";
import { useComposedRefs } from "@tamagui/compose-refs";
import {
  Theme,
  isTouchable,
  isWeb,
  themeable,
  useAnimationDriver,
  useEvent,
  useIsomorphicLayoutEffect,
  useThemeName
} from "@tamagui/core";
import { Portal } from "@tamagui/portal";
import { useKeyboardVisible } from "@tamagui/use-keyboard-visible";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Keyboard,
  PanResponder
} from "react-native";
import { HIDDEN_SIZE, SHEET_HIDDEN_STYLESHEET } from "./constants";
import { ParentSheetContext, SheetInsideSheetContext } from "./contexts";
import { resisted } from "./helpers";
import { SheetProvider } from "./SheetContext";
import { useSheetOpenState } from "./useSheetOpenState";
import { useSheetProviderProps } from "./useSheetProviderProps";
const SheetImplementationCustom = themeable(
  forwardRef(function SheetImplementationCustom2(props, forwardedRef) {
    const parentSheet = useContext(ParentSheetContext);
    const {
      animationConfig,
      modal = false,
      zIndex = parentSheet.zIndex + 1,
      moveOnKeyboardChange = false,
      unmountChildrenWhenHidden = false,
      portalProps
    } = props;
    const keyboardIsVisible = useKeyboardVisible();
    const state = useSheetOpenState(props);
    const [overlayComponent, setOverlayComponent] = useState(null);
    const providerProps = useSheetProviderProps(props, state, {
      onOverlayComponent: setOverlayComponent
    });
    const {
      frameSize,
      setFrameSize,
      snapPoints,
      position,
      setPosition,
      scrollBridge,
      screenSize,
      maxSnapPoint
    } = providerProps;
    const { open, controller, isHidden } = state;
    const sheetRef = useRef(null);
    const ref = useComposedRefs(forwardedRef, sheetRef);
    const [isShowingInnerSheet, setIsShowingInnerSheet] = useState(false);
    const shouldHideParentSheet = !isWeb && modal && isShowingInnerSheet;
    const parentSheetContext = useContext(SheetInsideSheetContext);
    const onInnerSheet = useCallback((hasChild) => {
      setIsShowingInnerSheet(hasChild);
    }, []);
    const positions = useMemo(
      () => snapPoints.map((point) => getPercentSize(point, screenSize)),
      [frameSize, snapPoints]
    );
    const driver = useAnimationDriver();
    const { useAnimatedNumber, useAnimatedNumberStyle, useAnimatedNumberReaction } = driver;
    const AnimatedView = driver["NumberView"] ?? driver.View;
    useIsomorphicLayoutEffect(() => {
      if (!(parentSheetContext && open))
        return;
      parentSheetContext(true);
      return () => {
        parentSheetContext(false);
      };
    }, [parentSheetContext, open]);
    const nextParentContext = useMemo(
      () => ({
        zIndex
      }),
      [zIndex]
    );
    const animatedNumber = useAnimatedNumber(HIDDEN_SIZE);
    const at = useRef(HIDDEN_SIZE);
    const currentId = useRef(0);
    useAnimatedNumberReaction(
      {
        value: animatedNumber,
        hostRef: sheetRef
      },
      (value) => {
        if (!driver.isReactNative)
          return;
        at.current = value;
        scrollBridge.paneY = value;
      }
    );
    function stopSpring() {
      animatedNumber.stop();
      if (scrollBridge.onFinishAnimate) {
        scrollBridge.onFinishAnimate();
        scrollBridge.onFinishAnimate = void 0;
      }
    }
    const hasntMeasured = at.current === HIDDEN_SIZE;
    const animateTo = useEvent((position2) => {
      if (frameSize === 0)
        return;
      const toValue = hasntMeasured || isHidden || position2 === -1 ? screenSize : positions[position2];
      if (at.current === toValue)
        return;
      stopSpring();
      if (hasntMeasured || isHidden) {
        animatedNumber.setValue(screenSize, {
          type: "timing",
          duration: 0
        });
        at.current = toValue;
        if (isHidden) {
          return;
        }
      }
      animatedNumber.setValue(toValue, {
        ...animationConfig,
        type: "spring"
      });
    });
    useIsomorphicLayoutEffect(() => {
      if (screenSize && hasntMeasured) {
        animatedNumber.setValue(screenSize, {
          type: "timing",
          duration: 0
        });
      }
    }, [hasntMeasured, screenSize]);
    useIsomorphicLayoutEffect(() => {
      if (!frameSize || isHidden || hasntMeasured && !open) {
        return;
      }
      animateTo(position);
    }, [isHidden, frameSize, open, position]);
    const disableDrag = props.disableDrag ?? (controller == null ? void 0 : controller.disableDrag);
    const themeName = useThemeName();
    const panResponder = useMemo(
      () => {
        if (disableDrag)
          return;
        if (!frameSize)
          return;
        if (isShowingInnerSheet)
          return;
        const minY = positions[0];
        scrollBridge.paneMinY = minY;
        let startY = at.current;
        function makeUnselectable(val) {
          if (!SHEET_HIDDEN_STYLESHEET)
            return;
          if (!val) {
            SHEET_HIDDEN_STYLESHEET.innerText = "";
          } else {
            SHEET_HIDDEN_STYLESHEET.innerText = ":root * { user-select: none !important; -webkit-user-select: none !important; }";
          }
        }
        const release = ({ vy, dragAt }) => {
          isExternalDrag = false;
          previouslyScrolling = false;
          makeUnselectable(false);
          const at2 = dragAt + startY;
          const end = at2 + frameSize * vy * 0.2;
          let closestPoint = 0;
          let dist = Infinity;
          for (let i = 0; i < positions.length; i++) {
            const position2 = positions[i];
            const curDist = end > position2 ? end - position2 : position2 - end;
            if (curDist < dist) {
              dist = curDist;
              closestPoint = i;
            }
          }
          setPosition(closestPoint);
          animateTo(closestPoint);
        };
        const finish = (_e, state2) => {
          release({
            vy: state2.vy,
            dragAt: state2.dy
          });
        };
        let previouslyScrolling = false;
        const onMoveShouldSet = (_e, { dy }) => {
          const isScrolled = scrollBridge.y !== 0;
          const isDraggingUp = dy < 0;
          const isNearTop = scrollBridge.paneY - 5 <= scrollBridge.paneMinY;
          if (isScrolled) {
            previouslyScrolling = true;
            return false;
          }
          if (isNearTop) {
            if (!isScrolled && isDraggingUp) {
              return false;
            }
          }
          return Math.abs(dy) > 5;
        };
        const grant = () => {
          makeUnselectable(true);
          stopSpring();
          startY = at.current;
        };
        let isExternalDrag = false;
        scrollBridge.drag = (dy) => {
          if (!isExternalDrag) {
            isExternalDrag = true;
            grant();
          }
          const to = dy + startY;
          animatedNumber.setValue(resisted(to, minY), { type: "direct" });
        };
        scrollBridge.release = release;
        return PanResponder.create({
          onMoveShouldSetPanResponder: onMoveShouldSet,
          onPanResponderGrant: grant,
          onPanResponderMove: (_e, { dy }) => {
            const toFull = dy + startY;
            const to = resisted(toFull, minY);
            animatedNumber.setValue(to, { type: "direct" });
          },
          onPanResponderEnd: finish,
          onPanResponderTerminate: finish,
          onPanResponderRelease: finish
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [disableDrag, isShowingInnerSheet, animateTo, frameSize, positions, setPosition]
    );
    const handleAnimationViewLayout = useCallback(
      (e) => {
        const next = (() => {
          var _a;
          let _ = (_a = e.nativeEvent) == null ? void 0 : _a.layout.height;
          if (isWeb && isTouchable && !open) {
            _ += 100;
          }
          return _;
        })();
        if (!next)
          return;
        setFrameSize(next);
      },
      [keyboardIsVisible]
    );
    const animatedStyle = useAnimatedNumberStyle(animatedNumber, (val) => {
      "worklet";
      const translateY = frameSize === 0 ? HIDDEN_SIZE : val;
      return {
        transform: [{ translateY }]
      };
    });
    const sizeBeforeKeyboard = useRef(null);
    useEffect(() => {
      if (isWeb || !moveOnKeyboardChange)
        return;
      const keyboardDidShowListener = Keyboard.addListener("keyboardDidShow", (e) => {
        if (sizeBeforeKeyboard.current !== null)
          return;
        sizeBeforeKeyboard.current = animatedNumber.getValue();
        animatedNumber.setValue(
          Math.max(animatedNumber.getValue() - e.endCoordinates.height, 0)
        );
      });
      const keyboardDidHideListener = Keyboard.addListener("keyboardDidHide", () => {
        if (sizeBeforeKeyboard.current === null)
          return;
        animatedNumber.setValue(sizeBeforeKeyboard.current);
        sizeBeforeKeyboard.current = null;
      });
      return () => {
        keyboardDidHideListener.remove();
        keyboardDidShowListener.remove();
      };
    }, [moveOnKeyboardChange]);
    const [opacity, setOpacity] = useState(open ? 1 : 0);
    if (open && opacity === 0) {
      setOpacity(1);
    }
    useEffect(() => {
      if (!open) {
        const tm = setTimeout(() => {
          setOpacity(0);
        }, 400);
        return () => {
          clearTimeout(tm);
        };
      }
    }, [open]);
    const contents = /* @__PURE__ */ jsx(ParentSheetContext.Provider, { value: nextParentContext, children: /* @__PURE__ */ jsxs(SheetProvider, { ...providerProps, children: [
      shouldHideParentSheet ? null : overlayComponent,
      /* @__PURE__ */ jsx(
        AnimatedView,
        {
          ref,
          ...panResponder == null ? void 0 : panResponder.panHandlers,
          onLayout: handleAnimationViewLayout,
          pointerEvents: open && !shouldHideParentSheet ? "auto" : "none",
          animation: props.animation,
          style: [
            {
              position: "absolute",
              zIndex,
              width: "100%",
              height: `${maxSnapPoint}%`,
              minHeight: `${maxSnapPoint}%`,
              opacity
            },
            animatedStyle
          ],
          children: props.children
        }
      )
    ] }) });
    const adaptContext = useContext(AdaptParentContext);
    const shouldMountChildren = Boolean(opacity || !unmountChildrenWhenHidden);
    if (modal) {
      const modalContents = /* @__PURE__ */ jsx(Portal, { zIndex, ...portalProps, children: shouldMountChildren && /* @__PURE__ */ jsx(Theme, { forceClassName: true, name: themeName, children: /* @__PURE__ */ jsx(AdaptParentContext.Provider, { value: adaptContext, children: contents }) }) });
      if (isWeb) {
        return modalContents;
      }
      return /* @__PURE__ */ jsx(SheetInsideSheetContext.Provider, { value: onInnerSheet, children: modalContents });
    }
    return contents;
  })
);
function getPercentSize(point, screenSize) {
  if (!screenSize)
    return 0;
  if (point === void 0) {
    console.warn("No snapPoint");
    return 0;
  }
  const pct = point / 100;
  const next = Math.round(screenSize - pct * screenSize);
  return next;
}
export {
  SheetImplementationCustom
};
//# sourceMappingURL=SheetImplementationCustom.js.map
